import { Icon, ICONS } from 'components/Icon';
import { Pressable } from 'components/Pressable';
import { useSelector } from 'hooks/useSelector';
import { FontFamilyMonoSemibold, theme } from 'lib/theme';
import * as React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { doShowSyncModal } from 'store/app/actions';
import { selectHasAuthToken } from 'store/auth/selectors';
import {
  selectHasAssetErrors,
  selectIsNotAuthorized,
} from 'store/error/selectors';
import { selectHasItemsToSync, selectSyncStatus } from 'store/sync/selectors';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { SyncSteps } from 'store/sync/types';
import Animated, {
  cancelAnimation,
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { selectIsOffline } from 'store/app/selectors';
import { DevIndicator } from './DevIndicator';
import { UpdateAvailableIndicator } from './UpdateAvailableIndicator';
import { TfaRequiredIndicator } from './TfaRequiredIndicator';

const CIRCLE_SIZE = 50;
const ICON_SIZE = CIRCLE_SIZE / 2;

type Props = {
  showSyncIcon: boolean;
};

export const SyncIndicator: React.FC<Props> = ({ showSyncIcon }) => {
  const dispatch = useDispatch();
  const syncingRef = useRef({
    is: false,
    was: false,
  });

  const toggleSyncDialog = useCallback(() => {
    dispatch(doShowSyncModal({ downloadCleanData: false }));
  }, [dispatch]);

  const hasItemsToSync = useSelector(selectHasItemsToSync);
  const hasAuthorizationToken = useSelector(selectHasAuthToken);
  const syncingStatus = useSelector(selectSyncStatus);
  const notAuthorized = useSelector(selectIsNotAuthorized);
  const isOfflineState = useSelector(selectIsOffline);
  const hasAssetErrors = useSelector(selectHasAssetErrors);

  if (syncingStatus.step !== SyncSteps.waiting) {
    syncingRef.current.was = true;
  }
  syncingRef.current.is = syncingStatus.step !== SyncSteps.waiting;

  // Just override this constant to see the different states (icon, colour)
  const typeForSyncIndicator =
    syncingStatus.step !== SyncSteps.waiting
      ? 'syncing'
      : isOfflineState
        ? 'networkerror'
        : notAuthorized
          ? 'autherror'
          : hasAssetErrors
            ? 'syncerror'
            : 'synced';

  const memoSyncIndicatorIcon = useMemo(
    () => <SyncIndicatorIcon type={typeForSyncIndicator} />,
    [typeForSyncIndicator],
  );

  if (!hasAuthorizationToken) {
    return null;
  }

  return (
    <View style={styles.bottomRightContainer}>
      <DevIndicator />
      <TfaRequiredIndicator />
      <UpdateAvailableIndicator />
      {showSyncIcon && (
        <View style={styles.syncContainer}>
          {hasItemsToSync && <View style={styles.changesBadge} />}
          <SyncIndicatorBody
            type={typeForSyncIndicator}
            SyncIcon={memoSyncIndicatorIcon}
            onPress={toggleSyncDialog}
            text={syncingStatus.text}
            count={`${syncingStatus.count}`}
            icon={syncingStatus.icon}
          />
        </View>
      )}
    </View>
  );
};

const SyncIndicatorBody = ({
  onPress,
  type,
  text,
  count,
  icon,
  SyncIcon,
}: {
  onPress: () => void;
  SyncIcon: React.ReactElement;
  type: 'syncing' | 'synced' | 'autherror' | 'syncerror' | 'networkerror';
  icon: ICONS | null;
  text: string;
  count: string;
}): React.ReactElement => {
  const backgroundColor =
    type === 'networkerror'
      ? '#999999'
      : type === 'autherror' || type === 'syncerror'
        ? theme.colors.red
        : theme.colors.green;

  const iconColor =
    type === 'networkerror' ? theme.colors.black : theme.colors.white;

  return (
    <Pressable
      onPress={onPress}
      style={[
        styles.container,
        {
          backgroundColor,
          shadowColor: backgroundColor,
        },
      ]}
      pressedStyle={{
        opacity: 0.5,
      }}
    >
      <ViewWithSeparators
        horizontal
        style={{ alignItems: 'center' }}
        separatorMargin={theme.paddingHorizontalHalf}
        separatorColor="transparent"
      >
        {SyncIcon}
        {!!text && (
          <Txt
            numberOfLines={2}
            style={[
              { color: theme.colors.white, textAlign: 'center' },
              Platform.OS !== 'web' ? { marginTop: 3 } : undefined,
            ]}
            lineHeightNone
            weight="bold"
            size="xsmall"
          >
            {text}
            {!!count && (
              <>
                {'\n'}
                <Txt
                  style={{
                    color: theme.colors.white,
                    fontFamily: FontFamilyMonoSemibold,
                  }}
                  lineHeightNone
                  size="xxxsmall"
                >
                  {count}
                </Txt>
              </>
            )}
          </Txt>
        )}
        {!!icon && <Icon source={icon} color={iconColor} />}
      </ViewWithSeparators>
    </Pressable>
  );
};

const SyncIndicatorIcon = ({
  type,
}: {
  type: 'syncing' | 'synced' | 'autherror' | 'syncerror' | 'networkerror';
}): React.ReactElement => {
  const rotationRef = useSharedValue(0);
  React.useEffect(() => {
    if (type === 'syncing') {
      rotationRef.value = withRepeat(
        withTiming(1, { duration: 1000, easing: Easing.linear }),
        0,
      );
    }
    return () => {
      if (type === 'syncing') {
        cancelAnimation(rotationRef);
        rotationRef.value = 0;
      }
    };
  }, [rotationRef, type]);

  const animatedStyles = useAnimatedStyle(() => {
    return {
      transform: [
        {
          rotate: `${interpolate(rotationRef.value, [0, 1], [0, 360])}deg`,
        },
      ],
    };
  });

  const iconSource =
    type === 'networkerror'
      ? ICONS.WifiSlashLight
      : type === 'autherror'
        ? ICONS.KeyLight
        : type === 'syncerror'
          ? ICONS.ExclamationSolid
          : ICONS.RotateDuotone;
  const iconColor =
    type === 'networkerror' ? theme.colors.black : theme.colors.white;

  return type !== 'syncing' ? (
    <Icon source={iconSource} size={ICON_SIZE} color={iconColor} />
  ) : (
    <Animated.View style={animatedStyles}>
      <Icon source={iconSource} size={ICON_SIZE} color={iconColor} />
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  changesBadge: {
    backgroundColor: theme.colors.red,
    borderColor: 'white',
    borderRadius: 7,
    borderWidth: 1,
    bottom: 0,
    elevation: 6,
    height: 14,
    position: 'absolute',
    right: 0,
    width: 14,
    zIndex: 1,
  },
  container: {
    borderRadius: CIRCLE_SIZE,
    elevation: 5,
    height: CIRCLE_SIZE,
    opacity: 0.8,
    shadowColor: theme.colors.black,
    shadowOffset: { height: 0, width: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 1,
    minWidth: CIRCLE_SIZE,
    paddingHorizontal: CIRCLE_SIZE / 4,
    justifyContent: 'center',
  },
  bottomRightContainer: {
    overflow: 'visible',
    position: 'absolute',
    bottom: 18,
    right: 18,
    flexDirection: 'row',
  },
  syncContainer: {
    position: 'relative',
    marginLeft: theme.paddingHorizontal / 2,
  },
});
